import React, { useState } from 'react';
import { CreditCard, Wallet, Smartphone, Building2, Coins, ArrowRight, Shield, Clock } from 'lucide-react';
import { mockData } from '../mock';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { CryptoPayment } from './CryptoPayment';
import { useToast } from '../hooks/use-toast';

export const PaymentSelection = ({ 
  amount, 
  orderType = 'subscription', 
  orderDetails = {},
  onPaymentCompleted = null,
  onClose = null 
}) => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const { toast } = useToast();

  const paymentIcons = {
    CreditCard: CreditCard,
    Wallet: Wallet,
    Smartphone: Smartphone,
    Building2: Building2,
    Coins: Coins
  };

  const handleMethodSelection = (methodId) => {
    setSelectedMethod(methodId);
    if (methodId === 'crypto') {
      setShowPaymentForm(true);
    } else {
      // For other payment methods, show coming soon or redirect to payment processor
      toast({
        title: "Payment Method Selected",
        description: `${getMethodName(methodId)} payment processing will be implemented in production.`,
      });
      
      // Simulate successful payment for demo
      setTimeout(() => {
        if (onPaymentCompleted) {
          onPaymentCompleted({
            payment_method: methodId,
            amount: amount,
            status: 'completed'
          });
        }
      }, 2000);
    }
  };

  const getMethodName = (methodId) => {
    const allMethods = [...mockData.paymentMethods.primary, ...mockData.paymentMethods.alternative];
    return allMethods.find(method => method.id === methodId)?.name || '';
  };

  const handleCryptoPaymentCompleted = (paymentData) => {
    if (onPaymentCompleted) {
      onPaymentCompleted(paymentData);
    }
  };

  if (showPaymentForm && selectedMethod === 'crypto') {
    return (
      <div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 className="heading-1">Cryptocurrency Payment</h2>
          <Button
            onClick={() => {
              setShowPaymentForm(false);
              setSelectedMethod('');
            }}
            variant="ghost"
            style={{ padding: '8px' }}
          >
            ← Back to Payment Methods
          </Button>
        </div>
        
        <CryptoPayment
          amount={amount.toString()}
          orderType={orderType}
          orderDetails={orderDetails}
          onPaymentCompleted={handleCryptoPaymentCompleted}
        />
      </div>
    );
  }

  return (
    <Card style={{
      border: '1px solid var(--border-light)',
      borderRadius: '0px',
      background: 'var(--bg-primary)'
    }}>
      <CardHeader>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <CardTitle className="heading-1">Choose Payment Method</CardTitle>
            <CardDescription className="body-regular">
              Select your preferred way to pay for {orderType}
            </CardDescription>
          </div>
          {onClose && (
            <Button onClick={onClose} variant="ghost" style={{ padding: '8px' }}>
              ✕
            </Button>
          )}
        </div>
        
        <div style={{
          background: 'var(--color-lavender)',
          padding: '16px',
          borderRadius: '0px',
          marginTop: '16px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="body-regular">Total Amount:</span>
            <span className="heading-2" style={{ color: 'var(--color-gold)' }}>
              ${amount} USD
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Primary Payment Methods */}
        <div style={{ marginBottom: '32px' }}>
          <h3 className="heading-3" style={{ marginBottom: '20px', color: 'var(--color-fence-green)' }}>
            Recommended Payment Methods
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {mockData.paymentMethods.primary.map((method) => {
              const IconComponent = paymentIcons[method.icon];
              return (
                <button
                  key={method.id}
                  onClick={() => handleMethodSelection(method.id)}
                  style={{
                    padding: '20px',
                    border: `2px solid ${selectedMethod === method.id ? 'var(--color-gold)' : 'var(--border-light)'}`,
                    background: selectedMethod === method.id ? 'var(--color-lavender)' : 'transparent',
                    borderRadius: '0px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                  className="hover-lift"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: method.popular ? 'var(--color-gold)' : 'var(--color-fence-green)',
                      borderRadius: '0px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconComponent size={24} color="white" />
                    </div>
                    
                    <div style={{ flex: '1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span className="heading-3">{method.name}</span>
                        {method.popular && (
                          <span style={{
                            background: 'var(--color-gold)',
                            color: 'white',
                            padding: '2px 8px',
                            fontSize: '10px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            Popular
                          </span>
                        )}
                      </div>
                      <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                        {method.description}
                      </p>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <span className="body-small" style={{ color: 'var(--color-fence-green)' }}>
                          <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          {method.processingTime}
                        </span>
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {method.fees}
                        </span>
                      </div>
                    </div>
                    
                    <ArrowRight size={20} color="var(--color-gold)" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Alternative Payment Methods */}
        <div>
          <h3 className="heading-3" style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Alternative Payment Methods
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {mockData.paymentMethods.alternative.map((method) => {
              const IconComponent = paymentIcons[method.icon];
              return (
                <button
                  key={method.id}
                  onClick={() => handleMethodSelection(method.id)}
                  style={{
                    padding: '20px',
                    border: `2px solid ${selectedMethod === method.id ? 'var(--color-gold)' : 'var(--border-light)'}`,
                    background: selectedMethod === method.id ? 'var(--color-lavender)' : 'transparent',
                    borderRadius: '0px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                  className="hover-lift"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'var(--color-fence-green)',
                      borderRadius: '0px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconComponent size={24} color="white" />
                    </div>
                    
                    <div style={{ flex: '1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span className="heading-3">{method.name}</span>
                      </div>
                      <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                        {method.description}
                      </p>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <span className="body-small" style={{ color: 'var(--color-fence-green)' }}>
                          <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          {method.processingTime}
                        </span>
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {method.fees}
                        </span>
                      </div>
                      {method.supportedCurrencies && (
                        <div style={{ marginTop: '8px' }}>
                          <span className="body-small" style={{ color: 'var(--color-gold)' }}>
                            Supports: {method.supportedCurrencies.join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <ArrowRight size={20} color="var(--color-gold)" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Security Notice */}
        <div style={{
          background: 'var(--bg-secondary)',
          padding: '16px',
          marginTop: '32px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Shield size={20} color="var(--color-fence-green)" />
          <div>
            <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>
              All payments are processed securely with industry-standard encryption. 
              Your payment information is never stored on our servers.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};